import { Check } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";

/**
 * Wizard step indicator.
 * Pass `onSelect(index)` to make steps clickable for direct navigation.
 * Wraps on narrow containers so long wizards (6+ steps) do not clip.
 */
export default function CreateWizardSteps({
  steps,
  current = 0,
  onSelect,
  testid = "create-wizard-steps",
}) {
  const clickable = typeof onSelect === "function";

  return (
    <ol
      className="flex flex-wrap items-center gap-1.5 mb-4"
      data-testid={testid}
    >
      {steps.map((label, i) => {
        const done = i < current;
        const active = i === current;
        const inner = (
          <>
            <span className={cn(
              "grid h-5 w-5 place-items-center rounded-full text-[10px] shrink-0",
              active && "bg-primary-foreground/20",
              done && "bg-primary text-primary-foreground",
              !active && !done && "bg-background border border-border",
            )}>
              {done ? <Check className="h-3 w-3" weight="bold" /> : i + 1}
            </span>
            <span className="truncate max-w-[7.5rem] sm:max-w-[9rem]">{label}</span>
          </>
        );
        const className = cn(
          "flex items-center gap-1.5 rounded-full px-2 py-1 text-[11px] sm:text-xs font-semibold transition-colors whitespace-nowrap",
          active && "bg-primary text-primary-foreground",
          done && "bg-primary/15 text-primary",
          !active && !done && "bg-muted text-muted-foreground",
          clickable && !active && "hover:bg-primary/20 hover:text-primary cursor-pointer",
          clickable && active && "cursor-default",
        );
        return (
          <li key={`${label}-${i}`} className="flex items-center shrink-0">
            {clickable ? (
              <button
                type="button"
                className={className}
                onClick={() => { if (i !== current) onSelect(i); }}
                aria-current={active ? "step" : undefined}
                data-testid={`${testid}-step-${i}`}
              >
                {inner}
              </button>
            ) : (
              <div className={className}>{inner}</div>
            )}
          </li>
        );
      })}
    </ol>
  );
}
